import {
    Box,
    FormLabel,
    Grid,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack,
    Text,
    Tooltip
} from "@chakra-ui/react";
import {useMutation, useQuery} from "@apollo/client";
import React, {KeyboardEvent, useContext, useEffect, useMemo, useState} from "react";
import {MdTranslate} from "react-icons/md";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {AreaLanguageOption, Spot, TextTranslation} from "../types";
import RichTextEditor from "../form/RichTextEditor";
import {getTextTranslationsQuery, setTextTranslationMutation} from "./textTranslationQueries";

type FieldType = "text" | "richText";

type TranslationField = {
    translationKey: string;
    label: string;
    inputType: FieldType;
    getSourceValue: () => string;
};

type ScopeOption = {
    id: string;
    label: string;
    clientId: string;
    fields: TranslationField[];
};

const SPACE_SCOPE_ID = "__space__";

const normalizeLanguageCode = (value?: string) => (value || "").trim().split("-")[0].toUpperCase();

const normalizeRichText = (value?: string) => {
    const html = (value || "").trim();
    if (!html) {
        return "";
    }
    if (typeof document === "undefined") {
        return html;
    }
    const container = document.createElement("div");
    container.innerHTML = html;
    const textContent = (container.textContent || "").replace(/\u00a0/g, " ").trim();
    if (!textContent) {
        return "";
    }
    return container.innerHTML.trim();
};

const buildFieldsForSpace = (
    getName: () => string,
    getSummary: () => string,
    getDescription: () => string
): TranslationField[] => [
    {translationKey: "area.name", label: "Name", inputType: "text", getSourceValue: getName},
    {translationKey: "area.summary", label: "Summary", inputType: "text", getSourceValue: getSummary},
    {translationKey: "area.description", label: "Description", inputType: "richText", getSourceValue: getDescription}
];

const buildFieldsForSpot = (spot: Spot): TranslationField[] => [
    {translationKey: "spot.name", label: "Name", inputType: "text", getSourceValue: () => spot.name || ""},
    {translationKey: "spot.summary", label: "Summary", inputType: "text", getSourceValue: () => spot.summary || ""},
    {translationKey: "spot.description", label: "Description", inputType: "richText", getSourceValue: () => spot.description || ""}
];

const renderSource = (field: TranslationField) => {
    const value = field.getSourceValue();
    if (!value.trim()) {
        return <Text fontSize={"sm"} color={"gray.500"}>No value</Text>;
    }
    if (field.inputType === "richText") {
        return <Box
            fontSize={"sm"}
            color={"gray.700"}
            dangerouslySetInnerHTML={{__html: normalizeRichText(value)}}
        />;
    }
    return <Text fontSize={"sm"} color={"gray.700"}>{value}</Text>;
};

const TranslationsManager = () => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const area = appContext.getArea();
    const spots = appContext.getSpots() || [];
    const [isOpen, setOpen] = useState<boolean>(false);
    const [scopeId, setScopeId] = useState<string>(SPACE_SCOPE_ID);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [translations, setTranslations] = useState<TextTranslation[]>([]);
    const [savingCellKey, setSavingCellKey] = useState<string | undefined>(undefined);
    const canEdit = appContext.isDraft();
    const publishedAt = appContext.getDisplayedVersion()?.publishedAt || 0;

    const areaLanguages: AreaLanguageOption[] = area?.languages?.available || [];
    const languageColumns = useMemo(() => {
        const uniqueByCode = new Map<string, AreaLanguageOption>();
        areaLanguages.forEach((language) => {
            const code = normalizeLanguageCode(language.code);
            if (!code) {
                return;
            }
            uniqueByCode.set(code, {...language, code: code});
        });
        return Array.from(uniqueByCode.values()).sort((a, b) => a.code.localeCompare(b.code));
    }, [areaLanguages]);

    const scopeOptions = useMemo<ScopeOption[]>(() => {
        if (!area) {
            return [];
        }
        const spaceScope: ScopeOption = {
            id: SPACE_SCOPE_ID,
            label: `Space — ${area.name || "Untitled"}`,
            clientId: area.id,
            fields: buildFieldsForSpace(
                () => area.name || "",
                () => area.summary || "",
                () => area.description || ""
            )
        };
        const spotScopes: ScopeOption[] = spots.map((spot) => ({
            id: `spot:${spot.key || spot.id}`,
            label: `Spot — ${spot.name || "Untitled"}`,
            clientId: spot.key || spot.id,
            fields: buildFieldsForSpot(spot)
        }));
        return [spaceScope, ...spotScopes];
    }, [area, spots]);

    const currentScope = useMemo(
        () => scopeOptions.find((option) => option.id === scopeId) || scopeOptions[0],
        [scopeOptions, scopeId]
    );

    useEffect(() => {
        if (isOpen && !scopeOptions.some((option) => option.id === scopeId)) {
            setScopeId(SPACE_SCOPE_ID);
        }
    }, [isOpen, scopeId, scopeOptions]);

    const languageCodes = languageColumns.map((language) => language.code);
    const translationKeys = currentScope?.fields.map((field) => field.translationKey) || [];

    const {data, loading} = useQuery(getTextTranslationsQuery, {
        variables: {
            areaId: area?.id,
            clientIds: currentScope?.clientId ? [currentScope.clientId] : [],
            languageCodes: languageCodes,
            translationKeys: translationKeys,
            publishedAt: publishedAt
        },
        skip: !isOpen || !area?.id || !currentScope || languageCodes.length === 0 || translationKeys.length === 0,
        fetchPolicy: "network-only"
    });

    useEffect(() => {
        const rows: TextTranslation[] = data?.getTextTranslations || [];
        setTranslations(rows);
    }, [data]);

    useEffect(() => {
        setTranslations([]);
    }, [scopeId]);

    const translationsByLangKey = useMemo(() => {
        const map = new Map<string, string>();
        translations.forEach((translation) => {
            const languageCode = normalizeLanguageCode(translation.languageCode);
            map.set(`${languageCode}:${translation.translationKey}`, translation.text || "");
        });
        return map;
    }, [translations]);

    const cellKey = (languageCode: string, translationKey: string) => `${languageCode}:${translationKey}`;

    useEffect(() => {
        const nextDrafts: Record<string, string> = {};
        (currentScope?.fields || []).forEach((field) => {
            languageColumns.forEach((language) => {
                const key = cellKey(language.code, field.translationKey);
                nextDrafts[key] = translationsByLangKey.get(key) || "";
            });
        });
        setDrafts(nextDrafts);
    }, [currentScope, languageColumns, translationsByLangKey]);

    const [setTextTranslation] = useMutation(setTextTranslationMutation);

    const mergeSaved = (saved: TextTranslation) => {
        setTranslations((previous) => {
            const filtered = previous.filter((entry) => entry.id !== saved.id &&
                !(entry.clientId === saved.clientId &&
                    normalizeLanguageCode(entry.languageCode) === normalizeLanguageCode(saved.languageCode) &&
                    entry.translationKey === saved.translationKey));
            return [...filtered, saved];
        });
    };

    const saveCell = async (field: TranslationField, languageCode: string, nextValue: string) => {
        if (!canEdit || !area?.id || !currentScope) {
            return;
        }
        const normalized = field.inputType === "richText" ? normalizeRichText(nextValue) : nextValue;
        const key = cellKey(languageCode, field.translationKey);
        const stored = translationsByLangKey.get(key) || "";
        if (normalized === stored) {
            return;
        }
        setSavingCellKey(key);
        try {
            const result = await setTextTranslation({
                variables: {
                    areaId: area.id,
                    clientId: currentScope.clientId,
                    languageCode: languageCode,
                    translationKey: field.translationKey,
                    text: normalized
                }
            });
            const saved = result.data?.setTextTranslation as TextTranslation | undefined;
            if (saved) {
                mergeSaved(saved);
            }
        } finally {
            setSavingCellKey(undefined);
        }
    };

    const columnCount = languageColumns.length + 1;
    const gridTemplateColumns = `minmax(180px, 1fr) ${languageColumns.map(() => "minmax(220px, 1fr)").join(" ")}`;

    return (
        <>
            <Tooltip label={"Translations"} hasArrow>
                <IconButton
                    aria-label={"Translations"}
                    icon={<MdTranslate/>}
                    variant={"ghost"}
                    size={"sm"}
                    onClick={() => setOpen(true)}
                />
            </Tooltip>
            <Modal isOpen={isOpen} onClose={() => setOpen(false)} isCentered size={"6xl"} blockScrollOnMount={false}>
                <ModalOverlay/>
                <ModalContent maxW={"90vw"} height={"90vh"}>
                    <ModalHeader>Translations</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody height={"100%"} overflowY={"auto"}>
                        <Stack spacing={5}>
                            <Box maxW={"420px"}>
                                <FormLabel marginBottom={1}>Scope</FormLabel>
                                <Select
                                    value={scopeId}
                                    onChange={(event) => setScopeId(event.target.value)}
                                    size={"sm"}
                                >
                                    {scopeOptions.map((option) => (
                                        <option key={option.id} value={option.id}>{option.label}</option>
                                    ))}
                                </Select>
                            </Box>

                            {languageColumns.length === 0 ? (
                                <Text fontSize={"sm"} color={"gray.600"}>
                                    Add space languages before managing translations.
                                </Text>
                            ) : !currentScope ? null : (
                                <Box>
                                    <Grid
                                        templateColumns={gridTemplateColumns}
                                        gap={3}
                                        alignItems={"stretch"}
                                    >
                                        <Box paddingBottom={2} borderBottomWidth={"1px"} borderColor={"gray.200"}>
                                            <Text fontSize={"xs"} fontWeight={"semibold"} textTransform={"uppercase"} color={"gray.600"}>
                                                Default text
                                            </Text>
                                        </Box>
                                        {languageColumns.map((language) => (
                                            <Box key={`header:${language.code}`} paddingBottom={2} borderBottomWidth={"1px"} borderColor={"gray.200"}>
                                                <Text fontSize={"xs"} fontWeight={"semibold"} textTransform={"uppercase"} color={"gray.600"}>
                                                    {language.label} ({language.code})
                                                </Text>
                                            </Box>
                                        ))}

                                        {currentScope.fields.map((field) => (
                                            <React.Fragment key={field.translationKey}>
                                                <Box gridColumn={`1 / span ${columnCount}`} paddingTop={4}>
                                                    <Text fontWeight={"medium"}>{field.label}</Text>
                                                </Box>
                                                <Box padding={2} borderWidth={"1px"} borderColor={"gray.100"} borderRadius={"md"} backgroundColor={"gray.50"}>
                                                    {renderSource(field)}
                                                </Box>
                                                {languageColumns.map((language) => {
                                                    const key = cellKey(language.code, field.translationKey);
                                                    const value = drafts[key] || "";
                                                    const isSaving = savingCellKey === key;
                                                    return (
                                                        <Box key={key} position={"relative"}>
                                                            {field.inputType === "richText" ? (
                                                                <RichTextEditor
                                                                    value={value}
                                                                    onChange={(nextValue) => setDrafts((previous) => ({
                                                                        ...previous,
                                                                        [key]: nextValue
                                                                    }))}
                                                                    onBlur={(nextValue) => saveCell(field, language.code, nextValue)}
                                                                    isDisabled={!canEdit}
                                                                    minHeight={"90px"}
                                                                />
                                                            ) : (
                                                                <Input
                                                                    size={"sm"}
                                                                    value={value}
                                                                    onChange={(event) => setDrafts((previous) => ({
                                                                        ...previous,
                                                                        [key]: event.target.value
                                                                    }))}
                                                                    onBlur={(event) => saveCell(field, language.code, event.target.value)}
                                                                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                                                        if (event.key === "Enter") {
                                                                            event.currentTarget.blur();
                                                                        }
                                                                    }}
                                                                    disabled={!canEdit}
                                                                />
                                                            )}
                                                            {isSaving ? (
                                                                <Text position={"absolute"} top={"-16px"} right={0} fontSize={"xs"} color={"blue.500"}>
                                                                    Saving…
                                                                </Text>
                                                            ) : null}
                                                        </Box>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </Grid>
                                    {loading ? (
                                        <Text marginTop={3} fontSize={"sm"} color={"gray.500"}>Loading translations…</Text>
                                    ) : null}
                                </Box>
                            )}

                            {!canEdit ? (
                                <Text fontSize={"sm"} color={"orange.600"}>
                                    Translations can only be updated while editing a draft.
                                </Text>
                            ) : null}
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize={"xs"} color={"gray.500"} marginRight={"auto"}>
                            Changes save automatically when you leave a field.
                        </Text>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default TranslationsManager;
